import React from 'react';
import { trackFreeConsultation } from '../utils/gtag';
import { Helmet } from 'react-helmet-async';
import { Calendar, MapPin, Video, ArrowRight, MessageCircle } from 'lucide-react';

const DISCOVERY_CALL_URL = 'https://calendly.com/sharon-sharonclare-hypnotherapy/discovery-call';
const BELFAST_BOOKING_URL = 'https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37';
// Lisburn: Sharon chose her Arzen Collective practitioner profile (not the Phorest
// direct link) as it's easier to navigate. Confirmed 2026-08-13.
const LISBURN_BOOKING_URL = 'https://arzencollective.co.uk/practitioner-profile/sharon-clare-hypnotherapy';

const Book: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Book an Appointment | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Book a hypnotherapy appointment with Sharon Clare at the Belfast Wellness Clinic or The Arzen Collective in Lisburn, or arrange a free 15-minute discovery call. Online appointments available."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/book/" />
        <meta property="og:title" content="Book an Appointment | Sharon Clare Hypnotherapy" />
        <meta
          property="og:description"
          content="Book a hypnotherapy appointment at the Belfast Wellness Clinic or The Arzen Collective in Lisburn, or arrange a free discovery call."
        />
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Book an Appointment
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Each clinic manages its own appointments, so you&rsquo;ll be taken directly to their
              secure online booking system. Choose whichever is most convenient for you.
            </p>
          </div>
        </div>
      </section>

      {/* Discovery Call */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 sm:p-10">
              <div className="flex items-start space-x-4">
                <MessageCircle className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-2">
                    Not sure where to start?
                  </h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    If you&rsquo;d like to ask a few questions before booking, you&rsquo;re welcome to
                    arrange a free 15-minute discovery call. It&rsquo;s a chance to discuss what
                    you&rsquo;d like help with, find out how solution-focused hypnotherapy works, and
                    decide whether it&rsquo;s the right approach for you. There&rsquo;s no obligation
                    to book an appointment afterwards.
                  </p>
                  <a
                    href={DISCOVERY_CALL_URL}
                    onClick={() => trackFreeConsultation()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Book a Free Discovery Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic booking */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
                Ready to book a consultation or session?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                I work from two welcoming clinic locations. Please choose the one that suits you best.
                Online appointments can be booked through either clinic.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Belfast */}
              <div className="bg-white rounded-2xl p-8 shadow-lg flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <MapPin className="w-6 h-6 text-accent-600" />
                  <h3 className="text-xl font-heading font-semibold text-gray-900">
                    Belfast Wellness Clinic
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">228 Ormeau Road, Belfast BT7 2FZ</p>
                <p className="text-gray-700 font-medium mb-2">Appointments available for:</p>
                <ul className="text-gray-700 space-y-1 mb-6 flex-grow">
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-500 rounded-full mr-3" />Initial Consultation</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-500 rounded-full mr-3" />Hypnotherapy Sessions</li>
                  <li className="flex items-center"><Video className="w-4 h-4 text-accent-500 mr-2.5" />Online Appointments</li>
                </ul>
                <a
                  href={BELFAST_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center w-full"
                >
                  Book via Belfast Wellness Clinic
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>

              {/* Lisburn */}
              <div className="bg-white rounded-2xl p-8 shadow-lg flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <MapPin className="w-6 h-6 text-accent-600" />
                  <h3 className="text-xl font-heading font-semibold text-gray-900">
                    The Arzen Collective — Lisburn
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">13B Antrim Street, Lisburn BT28</p>
                <p className="text-gray-700 font-medium mb-2">Appointments available for:</p>
                <ul className="text-gray-700 space-y-1 mb-6 flex-grow">
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-500 rounded-full mr-3" />Initial Consultation</li>
                  <li className="flex items-center"><span className="w-1.5 h-1.5 bg-accent-500 rounded-full mr-3" />Hypnotherapy Sessions</li>
                  <li className="flex items-center"><Video className="w-4 h-4 text-accent-500 mr-2.5" />Online Appointments</li>
                </ul>
                <a
                  href={LISBURN_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center w-full"
                >
                  Book via The Arzen Collective
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>

            <p className="text-center text-gray-600 mt-10">
              If you&rsquo;re unsure which clinic is the best fit, or have any questions before
              booking, please{' '}
              <a href="/contact" className="text-accent-600 hover:text-accent-700 underline">
                get in touch
              </a>{' '}
              and I&rsquo;ll be happy to help.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Book;
